// Step auto-framing: while scrubbing a process, a step whose participants sit
// outside the viewport moves the camera to frame them — remembering the
// viewport it left, and returning to it as soon as a step's participants fit
// that remembered view again (or the scrubber closes). A manual gesture
// forgets the remembered viewport: the user's new view becomes home.
//
// Runs ONLY when the step identity changes — never on viewport churn, so a
// mid-step pan is not fought.

import { useEffect, useRef } from "react";
import { pairFitsViewport } from "@archlang/scene/board";
import type { SpaceLike, Viewport } from "@archlang/scene/board";

export interface StepAutoFrameDeps {
  readonly source: SpaceLike;
  readonly getViewport: () => Viewport;
  readonly screenSize: () => { width: number; height: number };
  readonly focalPathOf: (id: string) => readonly string[] | null;
  readonly framePair: (aId: string, bId: string) => void;
  readonly restoreViewport: (vp: Viewport) => void;
}

export function useStepAutoFrame(
  stepParticipants: { readonly aId: string; readonly bId: string } | null | undefined,
  deps: StepAutoFrameDeps,
): { noteUserGesture: () => void } {
  const savedRef = useRef<Viewport | null>(null);
  const depsRef = useRef(deps);
  depsRef.current = deps;

  const pairKey = stepParticipants ? `${stepParticipants.aId}→${stepParticipants.bId}` : null;
  const pairRef = useRef(stepParticipants);
  pairRef.current = stepParticipants;

  useEffect(() => {
    const d = depsRef.current;
    const pair = pairRef.current;
    const screen = d.screenSize();
    if (screen.width === 0 || screen.height === 0) return;
    if (!pair) {
      // Scrubber closed / no step: go home if we ever left it.
      if (savedRef.current) {
        d.restoreViewport(savedRef.current);
        savedRef.current = null;
      }
      return;
    }
    // Home first: the remembered viewport wins the moment a step fits it.
    if (
      savedRef.current &&
      pairFitsViewport(d.source, pair.aId, pair.bId, savedRef.current, screen, d.focalPathOf) === true
    ) {
      d.restoreViewport(savedRef.current);
      savedRef.current = null;
      return;
    }
    // Already visible (or unresolvable — extern actor): don't move.
    const fits = pairFitsViewport(d.source, pair.aId, pair.bId, d.getViewport(), screen, d.focalPathOf);
    if (fits !== false) return;
    if (!savedRef.current) savedRef.current = d.getViewport();
    d.framePair(pair.aId, pair.bId);
    // The step identity is the ONLY trigger (see header).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pairKey]);

  // A manual pan/zoom makes the user's view the new home.
  const noteUserGesture = useRef(() => {
    savedRef.current = null;
  }).current;
  return { noteUserGesture };
}
